/**
 * 匿名セッションID（sessionId）の生成・保持（効果分析ログ、BR-AN-3(b)）。
 *
 * <p>同一訪問（タブを閉じるまで＝1訪問）のファネルイベントを相関させる匿名IDを提供する。
 * `sessionStorage`（キー `spica_sid`）に `crypto.randomUUID()` を保存・再利用し、使えない環境では
 * 多段フォールバック（getRandomValuesベース → メモリ上の一時ID）で必ず何らかのIDを返す。
 *
 * <p><b>フェイルオープン（NFR-AN-AVAIL1・BR-AN-10）</b>: 本モジュールは例外を投げない。
 * `sessionStorage` が使用不可（プライベートモード等でthrowする環境）でも送信を止めないため、
 * メモリ上の一時IDへフォールバックする（相関精度は落ちるがユーザー体験・送信は継続する）。
 *
 * <p><b>PII非含有（BR-AN-8）</b>: 値は個人を特定しないランダム値のみ。
 *
 * <p><b>iprosUuid（永続横断ID）はここで扱わない</b>: 既存Cookie `___IPROS_UUID_` を中継（polaris）が
 * `RequestContext.uuid` から付与するため、タグ側の送信・実装は不要（BR-AN-3(a)）。
 */

const SESSION_STORAGE_KEY = "spica_sid";

/** sessionStorage が使えない環境用のメモリ上フォールバックID（タグインスタンス存続中は一定）。 */
let memoryFallbackSessionId: string | null = null;

/**
 * ランダムなセッションIDを生成する。`crypto.randomUUID()` が使えない環境では
 * `crypto.getRandomValues` ベースのUUID相当を生成し、それも不可なら時刻＋乱数の簡易IDを返す。
 */
function generateSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // RFC4122 version 4 相当（バージョン/バリアントビットを設定）
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
      return (
        hex.slice(0, 4).join("") +
        "-" +
        hex.slice(4, 6).join("") +
        "-" +
        hex.slice(6, 8).join("") +
        "-" +
        hex.slice(8, 10).join("") +
        "-" +
        hex.slice(10, 16).join("")
      );
    }
  } catch {
    // crypto へのアクセス自体が失敗した場合も下の簡易生成へフォールバックする。
  }
  // 最終フォールバック（暗号学的強度は不要。相関キーとして一意性が保てれば十分）。
  return `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * 現在の訪問の匿名セッションIDを取得する。無ければ新規採番して保存する。
 *
 * <p>`sessionStorage` に保持済みなら再利用し、無ければ生成して保存する。`sessionStorage` が
 * 使用不可の場合はメモリ上の一時IDにフォールバックする（例外は投げない、フェイルオープン）。
 */
export function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const created = generateSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    // sessionStorage が使えない環境（プライベートモード等）: メモリ上の一時IDを使い回す。
    if (!memoryFallbackSessionId) {
      memoryFallbackSessionId = generateSessionId();
    }
    return memoryFallbackSessionId;
  }
}
